import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MailCheck } from "lucide-react";
import { AuthLayout, FormError, FormNotice, authInput } from "@/components/auth/AuthLayout";
import { api } from "@/lib/api";

export default function CheckEmail() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [cooldown, setCooldown] = useState(30);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const resend = async () => {
    setError("");
    setNotice("");
    try {
      await api.post("/auth/resend-verification", { email: email.trim() }, { auth: false });
      setNotice("If your account still needs verifying, a new link is on its way.");
      setCooldown(60);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AuthLayout testId="check-email-page" title="Check your email" subtitle="Verify your email address to start using ChatSleek.">
      <div className="space-y-4">
        <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-ink-850 p-4">
          <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-zinc-300" strokeWidth={1.6} />
          {location.state?.pending ? (
            <p className="text-sm leading-relaxed text-amber-200" data-testid="check-email-pending-message">
              Your sign-up was received, but we couldn't send the verification email to <span className="text-white">{email}</span> right now. Use "Resend verification email" in a few minutes.
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-zinc-300" data-testid="check-email-message">
              If <span className="text-white">{email || "your address"}</span> can be used, we've sent a verification link to it. Open the link on this device to finish signing up.
            </p>
          )}
        </div>
        {!location.state?.email && (
          <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={authInput} data-testid="check-email-input" />
        )}
        <FormNotice>{notice}</FormNotice>
        <FormError>{error}</FormError>
        <button
          type="button"
          onClick={resend}
          disabled={cooldown > 0 || !email.trim()}
          className="h-11 w-full rounded-xl border border-white/12 text-sm text-zinc-200 transition-colors hover:bg-white/[0.05] disabled:opacity-40"
          data-testid="check-email-resend-button"
        >
          {cooldown > 0 ? `Resend verification email in ${cooldown}s` : "Resend verification email"}
        </button>
        <Link to="/sign-in" className="block text-center text-sm text-zinc-400 hover:text-white" data-testid="check-email-sign-in-link">
          Back to sign in
        </Link>
      </div>
    </AuthLayout>
  );
}
